import { Chip } from "@itk/mui-components";
import { Box, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { ChipItem } from "./chip-list-types";

type Props = {
  labels: ChipItem[];
};

const ChipListBase = (props: Props) => {
  const { labels } = props;
  const theme = useTheme();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<(HTMLDivElement | null)[]>([]);
  const chipWidths = useRef<number[]>([]);

  const [width, setWidth] = useState(0);
  const [chipsToKeep, setChipsToKeep] = useState(labels.length);

  // No internal sort — render order follows the caller's array exactly, since it now encodes the
  // user's own drag-reordered View Settings preference (see view-settings.tsx). This also means the
  // caller's preferred order is what gets prioritized into the visible set below when width is tight.

  // "+N more" overflow chip keeps the original neutral styling — it's a UI affordance, not a labeled chip.
  const overflowChipSx = useMemo(
    () => ({
      bgcolor: theme.palette.customColors.lightGrey,
      ml: 1,
      borderRadius: 1,
      border: `1px solid ${theme.palette.customColors.grey2}`
    }),
    [theme]
  );

  // Same neutral fill for every chip, default text color — no per-text color assignment. Attribute
  // chips get a theme-primary border instead of the default grey one, so they still read as a
  // distinct chip type at a glance.
  const chipSxByLabel = useMemo(
    () =>
      labels.map(label => ({
        bgcolor: label.kind === "PRODUCT_TYPE" ? theme.palette.customColors.white : theme.palette.customColors.lightGrey,
        ml: 1,
        borderRadius: 1,
        border: `1px solid ${label.kind === "ATTRIBUTE" ? theme.palette.primary.main : theme.palette.customColors.grey2}`
      })),
    [labels, theme]
  );

  useEffect(() => {
    let frame = 0;
    const observer = new ResizeObserver(([entry]) => {
      const nextWidth = entry.contentRect.width;
      // The options panel animates its width and this list sits in the flex sibling that gives
      // way, so without coalescing every mounted ChipList re-measures on every frame of that
      // animation — and the measurement effect below reads offsetWidth, forcing a synchronous layout
      // each time. One rAF per observer, plus a sub-pixel threshold, caps that at one pass per frame.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setWidth(previous => (Math.abs(previous - nextWidth) < 1 ? previous : nextWidth)));
    });
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!width) return;

    chipRefs.current.forEach((el, i) => {
      if (el && el.offsetWidth > 0) {
        chipWidths.current[i] = el.offsetWidth;
      }
    });

    let total = 0;
    let count = 0;
    for (let i = 0; i < labels.length; i++) {
      const itemWidth = chipWidths.current[i] || 0;
      if (total + itemWidth < width - 100) {
        total += itemWidth;
        count++;
      } else {
        break;
      }
    }
    setChipsToKeep(count);
  }, [width, labels]);

  return (
    <Stack gap={1} direction="row" alignItems="center" flex={1} overflow="hidden" ref={wrapperRef}>
      {labels.map((label, i) => (
        <Box
          key={i}
          ref={(el: HTMLDivElement | null) => {
            chipRefs.current[i] = el;
          }}
          sx={{ display: i < chipsToKeep ? undefined : "none" }}
        >
          <Chip label={label.text} size="small" sx={chipSxByLabel[i]} />
        </Box>
      ))}
      {chipsToKeep < labels.length && (
        <Box>
          <Tooltip
            title={
              <Stack sx={{ gap: 0.5 }}>
                {labels.slice(chipsToKeep).map((label, index) => (
                  <Typography key={index} variant="caption" color="text.primary">
                    {label.text}
                  </Typography>
                ))}
              </Stack>
            }
            placement="bottom-start"
            slotProps={{
              tooltip: {
                sx: {
                  bgcolor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  boxShadow: theme.shadows[3],
                  border: `1px solid ${theme.palette.customColors.grey2}`,
                  p: 1.5
                }
              }
            }}
          >
            <span onClick={e => e.stopPropagation()}>
              <Chip label={`+${labels.length - chipsToKeep}`} size="small" sx={{ ...overflowChipSx, cursor: "default" }} />
            </span>
          </Tooltip>
        </Box>
      )}
    </Stack>
  );
};

// React.memo skips re-renders when labels is the same reference.
// Callers must memoize the labels array (e.g. useMemo) for this to have effect.
export const ChipList = memo(ChipListBase);
